import { FormEvent, useState } from "react"
import { router } from "@inertiajs/react"
import {
  Box, Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem,
  Select, Stack, TextField, Typography
} from "@mui/material"
import FileField from "../../../components/FileField"
import AudioPlayer from "../../../components/AudioPlayer"
import TranslationsEditor, { TranslationDraft, appendTranslationsFormData } from "../../../components/TranslationsEditor"

export type WordAudio = { url: string; language: string; ai_model: string; voice: string }

export type WordForm = {
  id: number | null
  kanji: string
  kana: string
  romaji: string
  romaji_obvious: boolean
  translations: TranslationDraft[]
  audio: WordAudio | null
  topic_ids: number[]
}

type Props = {
  word: WordForm
  topics: { id: number; name: string }[]
  errors?: Record<string, string[]>
}

export default function WordFormView({ word, topics, errors }: Props) {
  const isNew = !word.id
  const [kanji, setKanji] = useState(word.kanji ?? "")
  const [kana, setKana] = useState(word.kana ?? "")
  const [romaji, setRomaji] = useState(word.romaji ?? "")
  const [romajiObvious, setRomajiObvious] = useState(!!word.romaji_obvious)
  const [translations, setTranslations] = useState<TranslationDraft[]>(word.translations ?? [])
  const [audio, setAudio] = useState<File | null>(null)
  const [topicIds, setTopicIds] = useState<number[]>(word.topic_ids ?? [])

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    const data = new FormData()
    data.append("word[kanji]", kanji)
    data.append("word[kana]", kana)
    data.append("word[romaji]", romaji)
    data.append("word[romaji_obvious]", String(romajiObvious))
    appendTranslationsFormData(data, "word", translations)
    if (audio) data.append("word[audio]", audio)
    topicIds.forEach((id) => data.append("word[topic_ids][]", String(id)))
    if (topicIds.length === 0) data.append("word[topic_ids][]", "")

    const url = isNew ? "/admin/words" : `/admin/words/${word.id}`
    if (!isNew) data.append("_method", "patch")
    router.post(url, data, { forceFormData: true })
  }

  return (
    <Box component="form" onSubmit={onSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="h5">{isNew ? "New word" : "Edit word"}</Typography>
      <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
        <TextField label="Kanji" value={kanji} onChange={(e) => setKanji(e.target.value)} fullWidth
                   slotProps={{ htmlInput: { "data-testid": "word-kanji" } }}
                   error={!!errors?.kanji} helperText={errors?.kanji?.join(", ")} />
        <TextField label="Kana" value={kana} onChange={(e) => setKana(e.target.value)} fullWidth
                   slotProps={{ htmlInput: { "data-testid": "word-kana" } }}
                   error={!!errors?.kana} helperText={errors?.kana?.join(", ")} />
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} gap={2} alignItems="center">
        <TextField label="Romaji" value={romaji} onChange={(e) => setRomaji(e.target.value)} fullWidth
                   slotProps={{ htmlInput: { "data-testid": "word-romaji" } }} />
        <FormControlLabel
          control={<Checkbox checked={romajiObvious} onChange={(e) => setRomajiObvious(e.target.checked)}
                             data-testid="word-romaji-obvious" />}
          label="Obvious romaji" />
      </Stack>
      <Box>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Translations</Typography>
        <TranslationsEditor
          translations={translations} onChange={setTranslations}
          testIdPrefix="word-translation"
        />
      </Box>
      <FormControl fullWidth>
        <InputLabel id="word-topics-label">Topics</InputLabel>
        <Select
          labelId="word-topics-label" multiple value={topicIds} label="Topics"
          onChange={(e) => setTopicIds(e.target.value as number[])}
          renderValue={(selected) => topics.filter((t) => selected.includes(t.id)).map((t) => t.name).join(", ")}
        >
          {topics.map((t) => (
            <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Current audio</Typography>
        <AudioPlayer src={word.audio?.url ?? null} />
      </Box>
      <FileField label="Replace audio" accept="audio/*" name="audio"
                 onSelect={setAudio} selectedName={audio?.name ?? null}
                 existingUrl={word.audio?.url ?? null} testId="word-audio" />
      <Stack direction="row" gap={2}>
        <Button type="submit" variant="contained" data-testid="word-submit">
          {isNew ? "Create" : "Save changes"}
        </Button>
        <Button onClick={() => history.back()}>Cancel</Button>
      </Stack>
      {errors?.base && (
        <Typography color="error" data-testid="word-errors">{errors.base.join(", ")}</Typography>
      )}
    </Box>
  )
}
